import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@design-system/Collapsible';
import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { getFormattedDate } from '@/utils/date/getFormattedDate';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { SAMPLE_DETAILS_PATH } from '@/constants/paths';
import type { SampleTrackingInformation } from '@viome/viome-base/src/usecase/Misc/MiscModel';
import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react'; 

const SAMPLE_DATE_FORMAT = 'MMM DD, YYYY';
const SAMPLE_REGISTRATION_DATE_FORMAT = 'MMMM D, YYYY';

type ISampleData = {
  label: string;
  value?: string | number;
};

const renderSampleData = (sampleData: ISampleData[]) => {
  return sampleData.map((data) => {
    if (!data?.value) return null;

    return (
      <div key={data.label}>
        <span className="typography-title text-primary/70 mb-3">
          {data.label}
        </span>
        <p className="typography-body text-tertiary">{data.value}</p>
      </div>
    );
  });
};

interface SampleStatusCardProps extends React.ComponentProps<'div'> {
  sample: SampleTrackingInformation;
}

export const SampleStatusCard = ({ sample, ...rest }: SampleStatusCardProps) => {
  const { sample_information: sampleInfo, sample_status: status } = sample;
  const [isExpanded, setIsExpanded] =useState(false);
  
  const detailsPath = SAMPLE_DETAILS_PATH.replace(':id', sampleInfo.sample_id);

  const sampleData = [
    {
      label: 'Current Status',
      value: status.current_status || undefined,
    },
    {
      label: 'Received by Viome',
      value: getFormattedDate(status.recieved_by_viome_date, SAMPLE_DATE_FORMAT) === 'Invalid Date' ? status.recieved_by_viome_date : getFormattedDate(status.recieved_by_viome_date, SAMPLE_DATE_FORMAT),
    },
    {
      label: 'Lab processed',
      value: getFormattedDate(status.lab_processed_date, SAMPLE_DATE_FORMAT),
    },
    {
      label: 'Results analyzed',
      value: getFormattedDate(status.result_analysed_date, SAMPLE_DATE_FORMAT),
    },
    {
      label: 'Recommendations complete',
      value: getFormattedDate(status.recommendations_complete_date, SAMPLE_DATE_FORMAT),
    },
  ];

  return (
    <Card {...rest}>
      <CardHeader>
        <CardTitle className="typography-h3 text-primary">
          {getFormattedDate(
            status.registration_date,
            SAMPLE_REGISTRATION_DATE_FORMAT,
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-8">
        <div className="typography-body text-tertiary">
          <p className="typography-title text-secondary-info mb-2">
            {sampleInfo.test_name}
          </p>

          <p>
            Registration Date:{' '}
            {getFormattedDate(status.registration_date, 'MM/DD/YYYY')}
          </p>

          <p>Sample ID: {sampleInfo.sample_id}</p>
        </div>

        <Collapsible
          open={isExpanded}
          onOpenChange={setIsExpanded}
        >
          <CollapsibleContent className="flex flex-col gap-8">
            {renderSampleData(sampleData)}

            <p className="typography-body text-tertiary">
              After we receive your sample and complete questionnaires, it may
              take up to 4 weeks to generate your results.
            </p>

            <Button asChild className="w-full mb-8">
              <Link to={detailsPath}>
                Sample details
              </Link>
            </Button>
          </CollapsibleContent>

          <CollapsibleTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full flex items-center justify-center gap-2"
            >
              {isExpanded ? (
                <>
                  Show less
                  <ChevronUp className="h-5 w-5" />
                </>
              ) : (
                <>
                  Show more
                  <ChevronDown className="h-5 w-5" />
                </>
              )}
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
      </CardContent>
    </Card>
  );
};